import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Bell, AlertTriangle, Package, ArrowRightLeft, ClipboardList, X, CheckCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITY_STYLES = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700', icon: 'text-red-500' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', icon: 'text-amber-500' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500', text: 'text-blue-700', icon: 'text-blue-500' }
};

const TYPE_ICONS = {
    inventory_low: Package,
    transfer_pending: ArrowRightLeft,
    order_pending: ClipboardList
};

const NotificationBell = () => {
    const { token } = useAuth();
    const [open, setOpen] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [summary, setSummary] = useState({ total: 0, critical: 0, warning: 0, info: 0 });
    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const [dismissed, setDismissed] = useState(() => {
        try {
            const stored = localStorage.getItem('dismissed_alerts');
            return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch { return new Set(); }
    });
    const panelRef = useRef(null);

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const persistDismissed = (newSet) => {
        setDismissed(newSet);
        try { localStorage.setItem('dismissed_alerts', JSON.stringify([...newSet])); } catch {}
    };

    const fetchSummary = useCallback(async () => {
        if (!token) return;
        try {
            const res = await axios.get(`${API}/alerts/summary`, { headers });
            if (dismissed.size > 0) {
                const total = Math.max(0, (res.data.total || 0) - dismissed.size);
                setSummary({ ...res.data, total });
            } else {
                setSummary(res.data);
            }
        } catch { /* silent */ }
    }, [token, dismissed, headers]);

    const fetchAlerts = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API}/alerts`, { headers });
            const allAlerts = res.data.alerts || [];
            setAlerts(allAlerts);
            const active = allAlerts.filter(a => !dismissed.has(a.id));
            setSummary({
                total: active.length,
                critical: active.filter(a => a.severity === 'critical').length,
                warning: active.filter(a => a.severity === 'warning').length,
                info: active.filter(a => a.severity === 'info').length
            });
        } catch { /* silent */ } finally { setLoading(false); }
    }, [token, dismissed, headers]);

    const clearAlerts = () => {
        const ids = new Set(alerts.map(a => a.id));
        persistDismissed(ids);
        setSummary({ total: 0, critical: 0, warning: 0, info: 0 });
    };

    // Poll summary every 30s
    useEffect(() => {
        fetchSummary();
        const interval = setInterval(fetchSummary, 120000);
        return () => clearInterval(interval);
    }, [fetchSummary]);

    // Fetch full alerts when panel opens
    useEffect(() => {
        if (open) fetchAlerts();
    }, [open, fetchAlerts]);

    // Close panel on outside click
    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const visibleAlerts = alerts.filter(a => !dismissed.has(a.id));
    const filtered = activeFilter === 'all' ? visibleAlerts : visibleAlerts.filter(a => a.severity === activeFilter);
    const badgeCount = dismissed.size > 0 ? 0 : (summary.critical + summary.warning);

    return (
        <div className="relative" ref={panelRef}>
            <button
                data-testid="notification-bell"
                onClick={() => setOpen(!open)}
                className="relative p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white transition-colors"
            >
                <Bell className="w-5 h-5" />
                {badgeCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1 animate-pulse">
                        {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                )}
                {badgeCount === 0 && summary.info > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-96 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                        <div>
                            <h3 className="font-semibold text-sm text-slate-900">Alertas</h3>
                            <p className="text-xs text-slate-500">{visibleAlerts.length} alertas activas</p>
                        </div>
                        <div className="flex items-center gap-1">
                            {visibleAlerts.length > 0 && (
                                <button data-testid="clear-alerts-btn" onClick={clearAlerts} className="p-1.5 hover:bg-blue-100 rounded text-blue-600 transition-colors" title="Limpiar alertas">
                                    <CheckCheck className="w-4 h-4" />
                                </button>
                            )}
                            <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-200 rounded"><X className="w-4 h-4 text-slate-400" /></button>
                        </div>
                    </div>

                    {/* Summary badges */}
                    {summary.total > 0 && (
                        <div className="px-4 py-2 flex gap-2 border-b border-slate-100">
                            <button onClick={() => setActiveFilter('all')} className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${activeFilter === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                Todas ({summary.total})
                            </button>
                            {summary.critical > 0 && (
                                <button onClick={() => setActiveFilter('critical')} className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${activeFilter === 'critical' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>
                                    Críticas ({summary.critical})
                                </button>
                            )}
                            {summary.warning > 0 && (
                                <button onClick={() => setActiveFilter('warning')} className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${activeFilter === 'warning' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
                                    Alerta ({summary.warning})
                                </button>
                            )}
                            {summary.info > 0 && (
                                <button onClick={() => setActiveFilter('info')} className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${activeFilter === 'info' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
                                    Info ({summary.info})
                                </button>
                            )}
                        </div>
                    )}

                    {/* Alert list */}
                    <div className="max-h-80 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="py-8 text-center">
                                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                <p className="text-sm text-slate-500">No hay alertas</p>
                            </div>
                        ) : (
                            filtered.slice(0, 50).map((alert) => {
                                const styles = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
                                const Icon = TYPE_ICONS[alert.type] || AlertTriangle;
                                return (
                                    <div key={alert.id} className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3`} data-testid={`alert-item-${alert.id}`}>
                                        <div className={`w-8 h-8 rounded-lg ${styles.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                            <Icon className={`w-4 h-4 ${styles.icon}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`}></span>
                                                <p className="text-sm font-medium text-slate-900 truncate">{alert.title}</p>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-0.5">{alert.message}</p>
                                            <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded ${styles.bg} ${styles.text} font-medium`}>
                                                {alert.module === 'inventario' ? 'Inventario' : alert.module === 'ordenes' ? 'Órdenes' : alert.module}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {filtered.length > 50 && (
                        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-center">
                            <p className="text-xs text-slate-500">Mostrando 50 de {filtered.length} alertas</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
